import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Select, DatePicker, Table, Tag, Typography, Spin, Alert } from 'antd';
import {
    CameraOutlined, DollarOutlined, FileTextOutlined,
    PieChartOutlined, BarChartOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import api from '../../services/api';

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const RadiologyReports = () => {
    const { t } = useTranslation(['radiology', 'common']);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [dateRange, setDateRange] = useState([
        dayjs().startOf('month'),
        dayjs().endOf('month')
    ]);
    const [currency, setCurrency] = useState('');

    // Report data
    const [examData, setExamData] = useState(null);
    const [revenueData, setRevenueData] = useState(null);
    const [pendingData, setPendingData] = useState(null);

    useEffect(() => {
        fetchReports();
    }, [dateRange, currency]);

    const fetchReports = async () => {
        try {
            setLoading(true);
            setError('');

            const startDate = dateRange[0]?.format('YYYY-MM-DD') || '';
            const endDate = dateRange[1]?.format('YYYY-MM-DD') || '';
            const currencyParam = currency ? `&currency=${currency}` : '';

            const [examRes, revenueRes, pendingRes] = await Promise.all([
                api.get(`/radiology/reports/examinations?startDate=${startDate}&endDate=${endDate}`),
                api.get(`/radiology/reports/revenue?startDate=${startDate}&endDate=${endDate}${currencyParam}`),
                api.get('/radiology/reports/pending-payments')
            ]);

            setExamData(examRes.data?.data || {});
            setRevenueData(revenueRes.data?.data || {});
            setPendingData(pendingRes.data?.data || {});
        } catch (err) {
            setError(err.response?.data?.message || t('radiology:messages.reportsFetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    // Convert exam-by-type object to table data
    const examTypeData = examData?.examinationsByType
        ? Object.entries(examData.examinationsByType).map(([type, count]) => ({
            key: type,
            type,
            count
        }))
        : [];

    // Convert revenue-by-exam object to table data
    const revenueByExamData = revenueData?.revenueByExamType
        ? Object.entries(revenueData.revenueByExamType).map(([type, amount]) => ({
            key: type,
            type,
            amount
        }))
        : [];

    // Pending invoices table
    const pendingInvoices = pendingData?.invoices || [];

    const pendingColumns = [
        {
            title: t('radiology:reports.invoiceNumber'),
            dataIndex: 'invoiceNumber',
            key: 'invoiceNumber',
            render: (text) => <strong>{text || 'N/A'}</strong>
        },
        {
            title: t('radiology:reports.patient'),
            key: 'patient',
            render: (_, record) => record.patientId?.name || 'N/A'
        },
        {
            title: t('radiology:reports.total'),
            dataIndex: 'totalAmount',
            key: 'totalAmount',
            render: (amount, record) => {
                if (!amount) return '-';
                return record.currency === 'IQD'
                    ? `${amount.toLocaleString()} IQD`
                    : `$${amount.toFixed(2)}`;
            }
        },
        {
            title: t('radiology:reports.paid'),
            dataIndex: 'amountPaid',
            key: 'amountPaid',
            render: (amount, record) => {
                if (!amount) return '-';
                return record.currency === 'IQD'
                    ? `${amount.toLocaleString()} IQD`
                    : `$${amount.toFixed(2)}`;
            }
        },
        {
            title: t('radiology:reports.balanceDue'),
            dataIndex: 'balanceDue',
            key: 'balanceDue',
            render: (amount, record) => {
                if (!amount) return '-';
                return (
                    <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
                        {record.currency === 'IQD'
                            ? `${amount.toLocaleString()} IQD`
                            : `$${amount.toFixed(2)}`}
                    </span>
                );
            }
        },
        {
            title: t('radiology:reports.status'),
            dataIndex: 'paymentStatus',
            key: 'paymentStatus',
            render: (status) => (
                <Tag color={status === 'Partial' ? 'orange' : 'red'}>
                    {status}
                </Tag>
            )
        },
        {
            title: t('radiology:reports.date'),
            dataIndex: 'createdAt',
            key: 'createdAt',
            render: (date) => date ? dayjs(date).format('MMM DD, YYYY') : '-'
        }
    ];

    if (loading) {
        return (
            <div style={{ textAlign: 'center', marginTop: 100 }}>
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }}>
            <Title level={2}>{t('radiology:reports.title')}</Title>

            {error && (
                <Alert message={t('radiology:reports.error')} description={error} type="error" showIcon style={{ marginBottom: 16 }} />
            )}

            {/* Filters */}
            <Card style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]} align="middle">
                    <Col xs={24} sm={12} md={10}>
                        <span style={{ marginRight: 8 }}>{t('radiology:reports.dateRange')}</span>
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) => setDateRange(dates || [dayjs().startOf('month'), dayjs().endOf('month')])}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                        <span style={{ marginRight: 8 }}>{t('radiology:reports.currency')}</span>
                        <Select
                            placeholder={t('radiology:reports.allCurrencies')}
                            value={currency || undefined}
                            onChange={v => setCurrency(v || '')}
                            allowClear
                            style={{ width: 120 }}
                        >
                            <Option value="USD">USD</Option>
                            <Option value="IQD">IQD</Option>
                        </Select>
                    </Col>
                </Row>
            </Card>

            {/* Summary Stats */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:reports.totalExaminations')}
                            value={examData?.totalExaminations || 0}
                            prefix={<CameraOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:reports.totalRevenue')}
                            value={revenueData?.totalRevenue || 0}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:reports.totalPaid')}
                            value={revenueData?.totalPaid || 0}
                            prefix={<DollarOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                            precision={2}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} md={6}>
                    <Card>
                        <Statistic
                            title={t('radiology:reports.pendingAmount')}
                            value={pendingData?.totalPendingAmount || 0}
                            prefix={<FileTextOutlined />}
                            valueStyle={{ color: '#faad14' }}
                            precision={2}
                        />
                    </Card>
                </Col>
            </Row>

            {/* Examinations by Type & Revenue by Type */}
            <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                <Col xs={24} md={12}>
                    <Card
                        title={<span><PieChartOutlined style={{ marginRight: 8 }} />{t('radiology:reports.examinationsByType')}</span>}
                        size="small"
                    >
                        {examTypeData.length > 0 ? (
                            <Table
                                dataSource={examTypeData}
                                columns={[
                                    { title: t('radiology:reports.examType'), dataIndex: 'type', key: 'type', render: (t) => <Tag color="blue">{t}</Tag> },
                                    { title: t('radiology:reports.count'), dataIndex: 'count', key: 'count', render: (c) => <strong>{c}</strong> }
                                ]}
                                pagination={false}
                                size="small"
                            />
                        ) : (
                                <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>{t('radiology:reports.noExamData')}</div>
                        )}
                    </Card>
                </Col>
                <Col xs={24} md={12}>
                    <Card
                        title={<span><BarChartOutlined style={{ marginRight: 8 }} />{t('radiology:reports.revenueByExamType')}</span>}
                        size="small"
                    >
                        {revenueByExamData.length > 0 ? (
                            <Table
                                dataSource={revenueByExamData}
                                columns={[
                                    { title: t('radiology:reports.examType'), dataIndex: 'type', key: 'type', render: (t) => <Tag color="green">{t}</Tag> },
                                    {
                                        title: t('radiology:reports.revenue'),
                                        dataIndex: 'amount',
                                        key: 'amount',
                                        render: (a) => <strong>${(a || 0).toFixed(2)}</strong>
                                    }
                                ]}
                                pagination={false}
                                size="small"
                            />
                        ) : (
                                <div style={{ textAlign: 'center', padding: 24, color: '#999' }}>{t('radiology:reports.noRevenueData')}</div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Pending Payments */}
            <Card
                title={<span><FileTextOutlined style={{ marginRight: 8 }} />{t('radiology:reports.pendingPayments')} ({pendingInvoices.length})</span>}
            >
                <Table
                    dataSource={pendingInvoices}
                    columns={pendingColumns}
                    rowKey="_id"
                    pagination={{ pageSize: 10, showSizeChanger: true }}
                    locale={{ emptyText: t('radiology:reports.noPendingPayments') }}
                    size="small"
                />
            </Card>
        </div>
    );
};

export default RadiologyReports;
